import { useMemo, useState } from 'react';
import {
  ShieldCheck,
  RefreshCw,
  Loader2,
  RotateCcw,
  FlaskConical,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useHasPermission } from '@/client/hooks/useHasPermission';
import { useListFilters } from '@/client/hooks/useListFilters';
import {
  useValidatorCatchesQuery,
  useValidatorStuckQuery,
  useValidatorVerdictsQuery,
} from '@/client/api/workflows/workflows.query';
import {
  useDryRunValidationMutation,
  useReemitValidationsMutation,
} from '@/client/api/workflows/workflows.mutation';
import {
  StuckEntity,
  StuckFeedback,
  ValidationVerdict,
  ValidatorKind,
} from '@/client/types/workflows';
import { getApiErrorMessage } from '@/client/utils/errors';
import {
  WorkflowAccessDenied,
  formatDateTime,
  statusBadgeClass,
} from '@/client/components/admin/Workflows/workflowUi';

type Tab = 'verdicts' | 'catches' | 'stuck';

const isStuckEntity = (row: StuckEntity | StuckFeedback): row is StuckEntity =>
  'eid' in row && typeof (row as StuckEntity).eid === 'number';

const ValidatorPage = () => {
  useBreadcrumbs([
    { label: 'Admin', href: '/admin' },
    { label: 'Workflows', href: '/admin/workflows' },
    { label: 'Validator' },
  ]);

  const viewPerm = useHasPermission('workflow.view');
  const managePerm = useHasPermission('workflow.manage');
  const canView = viewPerm.isSuperAdmin || viewPerm.hasPermission;
  const canManage = managePerm.isSuperAdmin || managePerm.hasPermission;
  const permLoading = viewPerm.isLoading;

  // Kind, tab and the verdict/safety/since filters persist in the URL so a
  // reload restores the same view.
  const { filters, handleFilterChange } = useListFilters<{
    kind: ValidatorKind;
    tab: Tab;
    verdict: string;
    safety: string;
    since: string;
  }>({
    initialFilters: { kind: 'entity', tab: 'verdicts', verdict: '', safety: '', since: '24' },
  });
  const kind = filters.kind;
  const tab = filters.tab;
  const verdictFilter = filters.verdict;
  const safetyFilter = filters.safety;
  const sinceHours = Number(filters.since) || 24;
  const setTab = (id: Tab) => handleFilterChange('tab', id);
  const setVerdictFilter = (v: string) => handleFilterChange('verdict', v);
  const setSafetyFilter = (v: string) => handleFilterChange('safety', v);
  const setSinceHours = (n: number) => handleFilterChange('since', String(n));

  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [dryRunId, setDryRunId] = useState('');

  const verdictParams = useMemo(
    () => ({
      since_hours: sinceHours,
      limit: 100,
      ...(verdictFilter ? { verdict: verdictFilter } : {}),
      ...(safetyFilter ? { safety: safetyFilter } : {}),
    }),
    [sinceHours, verdictFilter, safetyFilter],
  );

  const verdictsQ = useValidatorVerdictsQuery(kind, verdictParams, canView && tab === 'verdicts');
  const catchesQ = useValidatorCatchesQuery(
    kind,
    { since_hours: 168, limit: 100 },
    canView && tab === 'catches',
  );
  const stuckQ = useValidatorStuckQuery(
    kind,
    { min_min: 5, max_min: 60, limit: 100 },
    canView && tab === 'stuck',
  );

  const reemit = useReemitValidationsMutation();
  const dryRun = useDryRunValidationMutation();

  const stuckRows = stuckQ.data?.stuck ?? [];

  const toggleId = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllStuck = () => {
    const ids = stuckRows.map((r) => (isStuckEntity(r) ? r.eid : r.fbid));
    setSelectedIds(new Set(ids));
  };

  const handleReemit = async () => {
    const ids = Array.from(selectedIds).slice(0, 50);
    if (ids.length === 0) {
      toast.error('Select at least one id', { richColors: true });
      return;
    }
    try {
      const res = await reemit.mutateAsync({ kind, body: { ids } });
      toast.success(
        `Reemit: ${res.enqueued} enqueued, ${res.not_found} not found, ${res.failed} failed`,
        { richColors: true },
      );
      setSelectedIds(new Set());
      stuckQ.refetch();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Reemit failed'), { richColors: true });
    }
  };

  const handleDryRun = async () => {
    const id = Number(dryRunId);
    if (!Number.isFinite(id) || id <= 0) {
      toast.error(`Enter a valid ${kind === 'entity' ? 'eid' : 'fbid'}`, { richColors: true });
      return;
    }
    try {
      const body =
        kind === 'entity' ? { id, eid: id } : { id, fbid: id };
      const res = await dryRun.mutateAsync({ kind, body });
      toast.success(`Dry-run enqueued (${res.idem.slice(0, 24)}…)`, {
        richColors: true,
        description: res.note,
      });
      setDryRunId('');
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Dry-run failed'), { richColors: true });
    }
  };

  if (permLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-screen-2xl space-y-4">
          <Skeleton className="h-10 w-56" />
          <Skeleton className="h-40 w-full" />
        </div>
      </div>
    );
  }

  if (!canView) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-screen-2xl">
          <WorkflowAccessDenied />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-screen-2xl space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-slate-900 text-white">
              <ShieldCheck className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">Validator</h1>
              <p className="mt-0.5 text-sm text-gray-500">
                Entity and feedback validation verdicts, safety catches, and stuck rows.
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="gap-2"
            onClick={() => {
              verdictsQ.refetch();
              catchesQ.refetch();
              stuckQ.refetch();
            }}
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="flex rounded-lg border border-gray-200 bg-white p-0.5 text-sm">
            {(['entity', 'feedback'] as const).map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => {
                  handleFilterChange('kind', k);
                  setSelectedIds(new Set());
                }}
                className={`rounded-md px-3 py-1.5 capitalize ${
                  kind === k ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                {k}
              </button>
            ))}
          </div>
          <div className="flex rounded-lg border border-gray-200 bg-white p-0.5 text-sm">
            {(
              [
                ['verdicts', 'Verdicts'],
                ['catches', 'Safety catches'],
                ['stuck', 'Stuck'],
              ] as const
            ).map(([id, label]) => (
              <button
                key={id}
                type="button"
                onClick={() => setTab(id)}
                className={`rounded-md px-3 py-1.5 ${
                  tab === id ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {canManage && (
          <div className="flex flex-wrap items-end gap-3 rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
            <div>
              <label className="text-xs font-medium text-gray-500">
                Dry-run {kind === 'entity' ? 'eid' : 'fbid'}
              </label>
              <input
                value={dryRunId}
                onChange={(e) => setDryRunId(e.target.value)}
                placeholder={kind === 'entity' ? '7895' : '21'}
                className="mt-1 block w-36 rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-gray-400"
              />
            </div>
            <Button
              size="sm"
              variant="outline"
              className="gap-2"
              disabled={dryRun.isPending}
              onClick={handleDryRun}
            >
              {dryRun.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <FlaskConical className="h-4 w-4" />
              )}
              Dry-run
            </Button>
            <p className="text-xs text-gray-400 max-w-md">
              Fires a non-mutating webhook (<code>dry_run=true</code>). Verdict appears with{' '}
              <code>n8n_execution_id LIKE &apos;dry:%&apos;</code>.
            </p>
          </div>
        )}

        {tab === 'verdicts' && (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2">
              <select
                value={verdictFilter}
                onChange={(e) => setVerdictFilter(e.target.value)}
                className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm"
              >
                <option value="">All verdicts</option>
                <option value="published">published</option>
                <option value="human_review">human_review</option>
                <option value="rejected">rejected</option>
              </select>
              <select
                value={safetyFilter}
                onChange={(e) => setSafetyFilter(e.target.value)}
                className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm"
              >
                <option value="">All safety</option>
                <option value="safe">safe</option>
                <option value="age_restricted">age_restricted</option>
                <option value="rejected">rejected</option>
                <option value="unavailable">unavailable</option>
              </select>
              <select
                value={sinceHours}
                onChange={(e) => setSinceHours(Number(e.target.value))}
                className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm"
              >
                <option value={24}>Last 24h</option>
                <option value={168}>Last 7d</option>
                <option value={720}>Last 30d</option>
              </select>
            </div>
            <VerdictsTable
              loading={verdictsQ.isLoading}
              error={verdictsQ.isError ? getApiErrorMessage(verdictsQ.error) : null}
              rows={verdictsQ.data?.verdicts ?? []}
              kind={kind}
            />
          </div>
        )}

        {tab === 'catches' && (
          <VerdictsTable
            loading={catchesQ.isLoading}
            error={catchesQ.isError ? getApiErrorMessage(catchesQ.error) : null}
            rows={catchesQ.data?.verdicts ?? []}
            kind={kind}
          />
        )}

        {tab === 'stuck' && (
          <div className="space-y-3">
            {canManage && (
              <div className="flex flex-wrap items-center gap-2">
                <Button size="sm" variant="outline" onClick={selectAllStuck}>
                  Select all ({stuckRows.length})
                </Button>
                <Button
                  size="sm"
                  className="gap-2"
                  disabled={selectedIds.size === 0 || reemit.isPending}
                  onClick={handleReemit}
                >
                  {reemit.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <RotateCcw className="h-4 w-4" />
                  )}
                  Reemit selected ({Math.min(selectedIds.size, 50)})
                </Button>
                <span className="text-xs text-gray-400">Batch capped at 50.</span>
              </div>
            )}
            <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
              {stuckQ.isLoading ? (
                <div className="space-y-2 p-4">
                  <Skeleton className="h-8 w-full" />
                </div>
              ) : stuckQ.isError ? (
                <p className="px-4 py-8 text-sm text-red-600">
                  {getApiErrorMessage(stuckQ.error, 'Failed to load stuck rows')}
                </p>
              ) : stuckRows.length === 0 ? (
                <p className="px-4 py-10 text-center text-sm text-gray-400">
                  Nothing stuck in the 5–60 minute window.
                </p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 text-left text-xs text-gray-500">
                      {canManage && <th className="px-4 py-2 font-medium w-10" />}
                      <th className="px-4 py-2 font-medium">
                        {kind === 'entity' ? 'eid' : 'fbid'}
                      </th>
                      <th className="px-4 py-2 font-medium">Version</th>
                      <th className="px-4 py-2 font-medium">uid</th>
                      <th className="px-4 py-2 font-medium">Added</th>
                      <th className="px-4 py-2 font-medium">Modified</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stuckRows.map((row) => {
                      const id = isStuckEntity(row) ? row.eid : row.fbid;
                      const version = isStuckEntity(row)
                        ? row.version
                        : (row.version ?? '—');
                      return (
                        <tr key={id} className="border-b border-gray-50 last:border-0">
                          {canManage && (
                            <td className="px-4 py-2">
                              <input
                                type="checkbox"
                                checked={selectedIds.has(id)}
                                onChange={() => toggleId(id)}
                              />
                            </td>
                          )}
                          <td className="px-4 py-2 font-mono text-xs">{id}</td>
                          <td className="px-4 py-2 text-gray-700">{version}</td>
                          <td className="px-4 py-2 text-gray-700">{row.uid ?? '—'}</td>
                          <td className="px-4 py-2 text-gray-600">
                            {formatDateTime(row.date_added)}
                          </td>
                          <td className="px-4 py-2 text-gray-600">
                            {formatDateTime(row.date_modified)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const VerdictsTable = ({
  loading,
  error,
  rows,
  kind,
}: {
  loading: boolean;
  error: string | null;
  rows: ValidationVerdict[];
  kind: ValidatorKind;
}) => (
  <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
    {loading ? (
      <div className="space-y-2 p-4">
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
      </div>
    ) : error ? (
      <p className="px-4 py-8 text-sm text-red-600">{error}</p>
    ) : rows.length === 0 ? (
      <p className="px-4 py-10 text-center text-sm text-gray-400">No rows in this window.</p>
    ) : (
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-100 text-left text-xs text-gray-500">
            <th className="px-4 py-2 font-medium">{kind === 'entity' ? 'eid' : 'fbid'}</th>
            <th className="px-4 py-2 font-medium">Verdict</th>
            <th className="px-4 py-2 font-medium">Safety</th>
            <th className="px-4 py-2 font-medium">Score</th>
            <th className="px-4 py-2 font-medium">Ruleset</th>
            <th className="px-4 py-2 font-medium">n8n exec</th>
            <th className="px-4 py-2 font-medium">Created</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((v, i) => {
            const id = kind === 'entity' ? v.eid : v.fbid;
            return (
              <tr
                key={`${v.n8n_execution_id ?? i}-${id}`}
                className="border-b border-gray-50 last:border-0"
              >
                <td className="px-4 py-2 font-mono text-xs">{id ?? '—'}</td>
                <td className="px-4 py-2">
                  <span
                    className={`inline-flex rounded-full border px-2 py-0.5 text-xs ${statusBadgeClass(v.verdict)}`}
                  >
                    {v.verdict}
                  </span>
                </td>
                <td className="px-4 py-2">
                  {v.safety_verdict ? (
                    <span
                      className={`inline-flex rounded-full border px-2 py-0.5 text-xs ${statusBadgeClass(v.safety_verdict)}`}
                    >
                      {v.safety_verdict}
                    </span>
                  ) : (
                    '—'
                  )}
                </td>
                <td className="px-4 py-2 text-gray-700">{v.score ?? '—'}</td>
                <td className="px-4 py-2 text-gray-600">{v.ruleset_version ?? '—'}</td>
                <td className="px-4 py-2 font-mono text-xs text-gray-500">
                  {v.n8n_execution_id ?? '—'}
                </td>
                <td className="px-4 py-2 text-gray-600">{formatDateTime(v.created_at)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    )}
  </div>
);

export default ValidatorPage;
